import { isCollabEntity, logger } from '@tunasong/models'
import { entitiesApi, skipToken, storageApi, useThunkDispatch } from '@tunasong/redux'
import type { Entity } from '@tunasong/schemas'
import type { CoreElement } from '@tunasong/schemas'
import { Y, getElementValue, getSlateYDocVersion } from '@tunasong/sync-lib'
import { toUint8Array } from 'js-base64'
import { useEffect, useMemo, useRef, useState } from 'react'
import invariant from 'tiny-invariant'

interface YDocState {
  doc: Y.Doc | undefined
  value: CoreElement[] | null
  v1: boolean
  v2: boolean
}

const initialYDocState: YDocState = { doc: undefined, value: null, v1: false, v2: false }

/** Create a collaborative YDoc from the specified entity */
export const useYDoc = <T extends Entity = Entity>(entity?: T) => {
  const isCollab = isCollabEntity(entity)

  // Disallow using this hook with non-collaborative entities
  if (entity && !isCollab) {
    throw new Error(`Entity is not a collaborative entity: ${entity.id}`)
  }

  const { id, yDoc: entityYDoc, storageUrls } = entity ?? {}

  const [{ doc, value, v1, v2 }, setDocState] = useState<YDocState>(initialYDocState)

  const useS3 = Boolean(storageUrls)

  // @deprecated Legacy support for yDoc as string inside the entity
  // Memoize to avoid creating a new Uint8Array every render, which would
  // re-trigger the effect below in an infinite loop.
  const entityYDocAsUpdates = useMemo(() => (entityYDoc ? toUint8Array(entityYDoc) : undefined), [entityYDoc])

  // New support for yDoc in S3 storage
  // Skip if the storageUrls have expired
  const validUrls = storageUrls && storageUrls.expiresAtISODateTime > new Date().toISOString()
  if (entity && !validUrls) {
    logger.debug(
      'Storage URLs are either not set or have expired. This can happen for new entities that are cached before a server response is received.',
      { id, storageUrls }
    )
  }

  // @important we must use currentData here to avoid using data from another entity, i.e., that params and data match
  // Gate on `validUrls` (expiry-aware), not mere presence: sending an expired signed URL to
  // CloudFront yields an edge-generated 403 with no CORS headers, surfaced to the browser as a
  // misleading "CORS error". The refetch effect below re-signs the URLs.
  const { currentData: yDocS3, ...restProps } = storageApi.useGetAuthorizedStorageFileQuery(
    !validUrls || !storageUrls ? skipToken : { storageUrls }
  )

  /**
   * Storage URLs are signed with a finite expiry, so any tab left open long
   * enough ends up holding an expired one. Skipping the query (above) avoids the
   * misleading CORS error but on its own leaves the doc permanently unloaded —
   * no fetch, no error, `isUninitialized` forever. Nothing else refetches: the
   * ref plugin's `shouldRefresh` only covers embedded ref blocks, not the entity
   * open in the collab editor. Ask the entities API to re-sign; the fresh
   * `storageUrls` arrive as a new `entity` prop and the query un-skips.
   *
   * Keyed on the expiry stamp so this fires once per stale value rather than
   * looping if the server hands back an equally-expired URL (e.g. clock skew).
   */
  const expiresAt = storageUrls?.expiresAtISODateTime
  const refetchedForExpiry = useRef<string | null>(null)
  const dispatch = useThunkDispatch()

  useEffect(() => {
    /** Only re-sign URLs that exist and have expired — absent URLs mean a new entity awaiting its first save. */
    if (!id || !expiresAt || validUrls) {
      return
    }
    const key = `${id}:${expiresAt}`
    if (refetchedForExpiry.current === key) {
      return
    }
    refetchedForExpiry.current = key
    logger.debug('Storage URLs expired — refetching the entity for freshly-signed URLs', { id, expiresAt })
    void dispatch(entitiesApi.endpoints.loadEntity.initiate({ id }, { forceRefetch: true }))
  }, [dispatch, expiresAt, id, validUrls])

  const docUpdates = useS3 ? yDocS3 : entityYDocAsUpdates

  /** Use a ref for doc so the effect doesn't re-trigger when doc changes
   *  from undefined to a newly created Y.Doc. */
  const docRef = useRef<Y.Doc | undefined>(doc)
  docRef.current = doc

  /**
   * Destroy the Y.Doc on unmount. The create-effect below only destroys the
   * OLD doc on an in-place id change; without this, unmounting orphaned the
   * live doc. The editor is keyed by `${id}-${updatedAt}` (editor-key.ts), so
   * every song-switch and every save REMOUNTS the collab editor — in
   * Performance mode, page-turning a setlist leaked one full-document Y.Doc per
   * song until the tab OOM'd on low-RAM devices. Children (the editor +
   * useYDocConnect) unmount and flush/disconnect BEFORE this parent cleanup, so
   * destroying here is safe.
   */
  useEffect(
    () => () => {
      docRef.current?.destroy()
    },
    []
  )

  /** If yDoc is set we apply the changes to the local doc */
  useEffect(() => {
    if (!id || !isCollab || !docUpdates) {
      return
    }

    const currentDoc = docRef.current

    const applyChanges = (doc: Y.Doc, updates: Uint8Array) => {
      invariant(doc, 'Y.Doc must be provided before applying changes')
      invariant(updates, 'Y updates must be provided')
      Y.applyUpdate(doc, updates)
    }

    /** If the ID has not changed, apply the changes to the existing doc */
    if (id === currentDoc?.guid) {
      // Not changed - get the local doc up to date with the updates
      logger.debug('Applying new updates to existing Y.Doc', { id })
      applyChanges(currentDoc, docUpdates)
      const newValue = getElementValue(currentDoc)
      const version = getSlateYDocVersion(currentDoc)
      setDocState({ doc: currentDoc, value: newValue, ...version })
      return
    }

    // Doc has changed, we need to destroy the old one
    if (currentDoc) {
      currentDoc.destroy()
    }
    const newDoc = new Y.Doc({ guid: id })

    logger.debug('Applying initial updates to newly created local Y.Doc', { id })
    applyChanges(newDoc, docUpdates)

    const newValue = getElementValue(newDoc)
    const version = getSlateYDocVersion(newDoc)
    setDocState({ doc: newDoc, value: newValue, ...version })
  }, [docUpdates, id, isCollab])

  /** We can never return a doc that belongs to another entity than provided to this hook */
  const verifiedIdDoc = doc && doc.guid === id ? doc : undefined

  // Only use the stored value/version when the doc matches the current entity ID
  const verifiedValue = verifiedIdDoc ? value : null
  const verifiedV1 = verifiedIdDoc ? v1 : false
  const verifiedV2 = verifiedIdDoc ? v2 : false

  // element is derived from the stable `value` in state — only changes when doc updates are applied
  const element = verifiedValue ? ({ ...entity, children: verifiedValue } as never as T) : null

  invariant(!verifiedIdDoc || verifiedIdDoc.guid === id, 'Y.Doc ID does not match entity ID')

  // If verifiedDoc is not set, value and element must be null
  invariant(!verifiedValue || (verifiedValue && verifiedIdDoc), 'Value must be undefined if doc is not set')
  invariant(!element || (element && verifiedIdDoc), 'Value must be undefined if doc is not set')

  /** Ensure we never return a doc for the wrong ID */
  return { doc: verifiedIdDoc, element, value: verifiedValue, v1: verifiedV1, v2: verifiedV2, ...restProps }
}
